import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { useParams, useHistory } from 'react-router';
import Helmet from 'react-helmet';
import Tab from '@tovia/man-ui/lib/components/Tab/Tab';
import Tabs from '@tovia/man-ui/lib/components/Tab/Tabs';
import { Comment } from 'src/client/components/ui/Comments/Comment';
import LoaderWrapper from '../../components/Loader/LoaderWrapper';
import { badgeMap } from 'src/client/helpers';
import { load as loadComments } from '../../redux/modules/userComments';
import { decodeDisplayName } from 'src/shared/converters';
import { rateItem } from '../../redux/modules/ratingInfo';
import { useSelector } from '../../redux/modules/helpers/useSelector';
import urls from 'src/shared/urls';
import moment from 'moment';

export const getTimeAgo = ({ date }) => moment(new Date(date)).fromNow();

const tab2params = {
  latest: {
    order: 'TIMESTAMP',
    direction: 'DESC',
  },
  top: {
    order: 'RATING',
    direction: 'DESC',
  },
  replies: {
    direction: 'DESC',
  },
};

const Div = styled.div`
  & .link {
    margin-left: 5px;
  }

  & .title {
    margin-bottom: 35px;
    margin-top: 35px;
  }

  & .body {
    min-height: 500px;
    position: relative;
  }

  & .grey {
    color: ${(props) => props.theme.primary7};
  }

  & .view-link {
    color: ${(props) => props.theme.primary7};
    font-size: 0.9rem;
    margin-bottom: 8.5px;
  }

  & .comment-actions {
    color: ${(props) => props.theme.primary7};
    :hover {
      color: unset;
    }

    &.active {
      font-weight: bold;
      color: ${(props) => props.theme.primaryBtn1Bg};
    }
  }
`;

export default function UserComments() {
  const [currentTab, setCurrentTab] = useState<string>('latest');

  const dispatch = useDispatch();
  const { displayName } = useParams<{ displayName: string }>();
  const history = useHistory();
  const intl = useIntl();

  const user = useSelector((state) => state.auth.user);
  const site = useSelector((state) => state.site);
  const ratings = useSelector((state) => state.ratingInfo.ratings);
  const { items, loading, error, loaded } = useSelector((state) => state.userComments);

  const loadData = useCallback(() => {
    const params = {
      displayname: decodeDisplayName(displayName),
      ...tab2params[currentTab],
      getParent: true,
      tab: currentTab,
    };

    dispatch(loadComments(params));
  }, [dispatch, displayName, currentTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [history, error]);

  const member = useMemo(() => user && user.validSubscription, [user]);

  const comment2Props = useCallback(
    (comment) => {
      const parentUrl = comment.object.path;
      const isRated = ratings[comment.UUID] === 1;
      const badgeId = comment.badges;

      return {
        UUID: comment.UUID,
        parentUUID: comment.parentUUID, // needed for nextComments
        parentUrl,
        onRate: () =>
          dispatch(
            rateItem({
              objectUUID: comment.UUID,
              objectType: 'COMMENT',
              rating: isRated ? 0 : 1, // toggle rating between 0 and 1
            }),
          ),
        isRated,
        ratesCount: comment.rating || 0,
        badge: badgeMap[badgeId],
        displayName: comment.userDisplayName,
        text: comment.text,
        timestamp: comment.timestamp,
        postStatus: '',
        childComments: [],
        onCommentSubmit: () => {},
      };
    },
    [dispatch, ratings],
  );

  const renderTabs = () => {
    const tabs = [
      {
        value: 'latest',
        name: intl.formatMessage({ id: 'comments.userComments.tabs.latest', defaultMessage: 'Latest' }),
      },
      {
        value: 'top',
        name: intl.formatMessage({ id: 'comments.userComments.tabs.topRated', defaultMessage: 'Top Rated' }),
      },
      ...(user && user.displayname === decodeDisplayName(displayName)
        ? [
            {
              value: 'replies',
              name: intl.formatMessage({ id: 'comments.userComments.tabs.replies', defaultMessage: 'Replies' }),
            },
          ]
        : []),
    ];

    return (
      <Tabs>
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...tab}
            active={currentTab === tab.value}
            onClick={() => setCurrentTab(tab.value)}
          />
        ))}
      </Tabs>
    );
  };

  const renderCommentLink = (comment) => {
    const link = comment.object.path;
    const linkText = comment.object.name;

    if (link && linkText) {
      const date = intl.formatDate(new Date(comment.timestamp), { year: 'numeric', month: 'long', day: '2-digit' });
      const on = intl.formatMessage({ id: 'comments.blogPanel.on', defaultMessage: 'on' });

      return (
        <div className="view-link">
          <span>{date}</span>
          {` ${on} `}
          <NavLink to={link}>{linkText}</NavLink>
        </div>
      );
    }
    return null;
  };

  return (
    <Div className="content container">
      <Helmet>
        <title>{`${site.name} - ${decodeDisplayName(displayName)}: Comments`}</title>
      </Helmet>
      <h2 className="title">
        {intl.formatMessage(
          { id: 'comments.commenters_comment', defaultMessage: `${decodeDisplayName(displayName)}'s Comments` },
          { name: decodeDisplayName(displayName) },
        )}
      </h2>
      {renderTabs()}
      <div className="body">
        {(loading || error) && (
          <LoaderWrapper
            defaultHeight
            reloadFunction={loadData}
            loadingStatus={loading}
            reduxErrorStatus={!loaded && error}
            contentType="comments"
          />
        )}
        {!loading &&
          !(!loaded && error) &&
          items.map((comment) => {
            const commentProps = comment2Props(comment);
            return (
              <Comment
                user={user}
                isUser={member}
                key={commentProps.UUID}
                UUID={commentProps.UUID}
                parentUUID={commentProps.parentUUID}
                parentUrl={commentProps.parentUrl}
                onRate={commentProps.onRate}
                isRated={commentProps.isRated}
                ratesCount={commentProps.ratesCount}
                badge={commentProps.badge}
                displayName={commentProps.displayName}
                text={commentProps.text}
                timestamp={commentProps.timestamp}
                postStatus={commentProps.postStatus}
                onCommentSubmit={commentProps.onCommentSubmit}
                childComments={commentProps.childComments}
                showUpgradeDialog={() => {}} // TODO: update man-ui to use it
                link={renderCommentLink(comment)}
                renderRelativeDate={(date) => getTimeAgo({ date })}
                replyText={intl.formatMessage({ id: 'comments.actions.reply', defaultMessage: 'Reply' })}
              />
            );
          })}
      </div>
    </Div>
  );
}
